/**
 * Cancelled-order login alert for reps.
 *
 * On entering the rep area, checks the rep's orders for any that were cancelled
 * and not yet acknowledged by this rep, and shows a single combined modal
 * listing each cancelled order with the reason it was cancelled. Dismissing the
 * modal marks those orders acknowledged (per-rep, in localStorage) so they don't
 * reappear; new cancellations surface on the next login.
 *
 * First run for a rep baselines the existing cancellations as already-seen, so
 * the modal only surfaces cancellations that happen from this point forward
 * (avoids dumping the historical backlog on first login).
 *
 * Mounted once in RepNavigation so it fires regardless of which rep page loads.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { CancelOutlined as CancelIcon } from '@mui/icons-material';
import { repAPI } from '../../services/api';
import { formatDateCST } from '../../utils/dateUtils';
import { logger } from '../../utils/logger';

const seenKey = (repId) => `rep_seen_cancelled_orders_${repId}`;

// Returns null when no record exists yet (first run), else an array of order ids.
const loadSeen = (repId) => {
  try {
    const raw = localStorage.getItem(seenKey(repId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveSeen = (repId, ids) => {
  try {
    localStorage.setItem(seenKey(repId), JSON.stringify(ids));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
};

const RepCancelledOrdersAlert = ({ currentUser }) => {
  const [newlyCancelled, setNewlyCancelled] = useState([]);
  const [open, setOpen] = useState(false);
  const repId = currentUser?.id;

  useEffect(() => {
    if (!repId) return undefined;
    let aborted = false;

    (async () => {
      try {
        const res = await repAPI.getRepOrders();
        if (aborted || !res?.success) return;

        const cancelledOrders = (res.data || [])
          .filter((o) => o.status === 'CANCELLED' && o.cancelledAt)
          .sort((a, b) => new Date(b.cancelledAt) - new Date(a.cancelledAt));

        const seen = loadSeen(repId);
        if (seen === null) {
          // First run: baseline existing cancellations so only future ones alert.
          saveSeen(repId, cancelledOrders.map((o) => o.id));
          return;
        }

        const seenSet = new Set(seen);
        const fresh = cancelledOrders.filter((o) => !seenSet.has(o.id));
        if (fresh.length > 0) {
          setNewlyCancelled(fresh);
          setOpen(true);
        }
      } catch (err) {
        logger.error('Failed to check for cancelled orders:', err);
      }
    })();

    return () => { aborted = true; };
  }, [repId]);

  const handleClose = useCallback(() => {
    if (repId) {
      const seen = loadSeen(repId) || [];
      const merged = Array.from(new Set([...seen, ...newlyCancelled.map((o) => o.id)]));
      saveSeen(repId, merged);
    }
    setOpen(false);
  }, [repId, newlyCancelled]);

  if (!open || newlyCancelled.length === 0) return null;

  const multiple = newlyCancelled.length > 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CancelIcon color="error" />
        {multiple ? `${newlyCancelled.length} orders were cancelled` : 'An order was cancelled'}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {multiple
            ? 'The following orders for your clients have been cancelled:'
            : 'The following order for one of your clients has been cancelled:'}
        </Typography>
        {newlyCancelled.map((order, i) => (
          <Box key={order.id} sx={{ mb: 2 }}>
            {i > 0 && <Divider sx={{ mb: 2 }} />}
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {order.orderNumber} — {order.user?.profile?.clinicName || order.user?.email || 'Client'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Cancelled {formatDateCST(order.cancelledAt)}
            </Typography>
            <Alert severity="error" icon={false} sx={{ py: 0.5 }}>
              <strong>Reason:</strong> {order.cancellationReason || 'No reason provided.'}
            </Alert>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">Got it</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepCancelledOrdersAlert;
